import React from 'react';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import { FeatureName } from '../../types/featureNames';
import AccessDenied from '../../pages/AccessDenied';

interface FeatureGuardProps {
    /**
     * The feature that must be enabled for the current user's tenant + role.
     */
    feature: FeatureName;
    /** Custom fallback rendered when the feature is not enabled.
     *  Defaults to <AccessDenied />. Set to null to hide entirely. */
    fallback?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * FeatureGuard — conditionally renders children based on tenant feature flags.
 *
 * Usage (page-level):
 *   <FeatureGuard feature={FeatureName.SHOW_LOGS}>
 *     <LogsPage />
 *   </FeatureGuard>
 *
 * Usage (inline, e.g. hide an element when feature is off):
 *   <FeatureGuard feature={FeatureName.SHOW_LOGS} fallback={null}>
 *     <LogsTab />
 *   </FeatureGuard>
 *
 * Feature names are always passed as typed enum values — nothing is hardcoded.
 */
const FeatureGuard: React.FC<FeatureGuardProps> = ({
    feature,
    fallback = <AccessDenied />,
    children,
}) => {
    const { hasFeature } = useFeatureFlags();

    if (!hasFeature(feature)) {
        return <>{fallback}</>;
    }

    return <>{children}</>;
};

export default FeatureGuard;
